import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { Candidate, PresenceState } from './logic'

/**
 * One engineer, with an honest reason for and against.
 *
 * Shared by the selection rail and the assign dialog so the two can never rank the same people
 * differently or describe them in different words — they are the same decision seen from two
 * places on the screen.
 *
 * The con line is not decoration. A card that only argued FOR its candidate would be a rigged
 * vote, and the thing command and control most needs to see is the cost of the obvious choice:
 * the last engineer leaving a site, the right skills in the wrong city.
 */

const DOT: Record<PresenceState, string> = {
  available: 'rgb(151,200,100)',
  busy: 'rgb(245,158,11)',
  away: 'hsl(215 16% 60%)',
  offline: 'hsl(215 16% 78%)',
}

export function PresenceDot({ state, className }: { state: PresenceState; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block h-2 w-2 flex-none rounded-full', className)}
      style={{ backgroundColor: DOT[state] }}
    />
  )
}

export function CandidateCard({ candidate, rank, selected, onSelect, showRadio = false }: {
  candidate: Candidate
  rank?: number
  selected?: boolean
  onSelect?: () => void
  showRadio?: boolean
}) {
  const { t } = useTranslation()
  const { engineer: e, presence, ineligible } = candidate
  const teams = [e.primaryTeam, ...e.otherTeams].filter(Boolean)

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!onSelect}
      aria-pressed={selected ? true : undefined}
      className={cn(
        'w-full rounded-lg border p-2.5 text-left transition-colors',
        selected ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/50',
        // Ineligible candidates stay on screen and stay readable — dimming them to nothing
        // would hide WHY a pool is empty, which is the thing worth knowing.
        ineligible && 'bg-muted/30',
        !onSelect && 'cursor-default',
      )}
    >
      <div className="flex items-start gap-2.5">
        {showRadio && (
          <span
            aria-hidden
            className={cn('mt-1 h-3.5 w-3.5 flex-none rounded-full border',
              selected ? 'border-[5px] border-primary' : 'border-border')}
          />
        )}
        {rank != null && (
          <span className="mt-0.5 w-4 flex-none text-xs font-semibold tabular-nums text-muted-foreground">{rank}</span>
        )}

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <PresenceDot state={presence} />
            <span className={cn('truncate text-sm font-semibold',
              (ineligible || presence === 'offline') && 'text-muted-foreground')}>
              {e.name ?? t('assignment.unnamedEngineer', 'Unnamed')}
            </span>
            {/* NOT LOGGED IN OUTRANKS EVERYTHING ELSE ON THE CARD. Whether somebody is available
                or busy is a question about a person who is HERE; someone who is not signed in
                cannot be handed work at all, and that was carried by nothing but a grey dot the
                same size as the green one. */}
            {presence === 'offline' && (
              <span className="flex-none rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                    style={{ backgroundColor: 'hsl(220 14% 90%)', color: 'hsl(215 16% 35%)' }}>
                {t('assignment.notLoggedIn', 'Not logged in')}
              </span>
            )}
            {ineligible && (
              <span className="flex-none rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ backgroundColor: 'rgba(232,96,55,0.12)', color: 'rgb(150,52,26)' }}>
                {t('assignment.notEligible', 'Not eligible')}
              </span>
            )}
          </span>

          <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
            {e.location ?? t('assignment.noSite', 'No site recorded')}
            {teams.length > 0 && ` · ${teams.join(', ')}`}
          </span>

          <span className="mt-0.5 block text-[11px] tabular-nums text-muted-foreground">
            {e.assignmentsTotal === 0
              ? t('assignment.nothingAssigned', 'Nothing assigned')
              : t('assignment.currentLoad', '{{total}} assigned · {{working}} under way',
                  { total: e.assignmentsTotal, working: e.assignmentsWorking })}
          </span>

          {/* WHICH object, not just how many — a count cannot tell you whether the person you are
              about to pull is holding something that matters more than the thing you are assigning. */}
          {(e.assignmentObjects ?? []).length > 0 && (
            <span className="mt-1 flex flex-wrap gap-1">
              {(e.assignmentObjects ?? []).slice(0, 3).map((a) => (
                <span key={a.objectId}
                      className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                      style={a.working
                        ? { backgroundColor: 'rgba(245,158,11,0.16)', color: 'rgb(146,88,8)' }
                        : { backgroundColor: 'hsl(220 14% 93%)', color: 'hsl(215 16% 40%)' }}
                      title={a.working
                        ? t('assignment.workingOn', 'Under way on {{id}}', { id: a.objectId })
                        : t('assignment.assignedTo', 'Assigned {{id}}, not started', { id: a.objectId })}>
                  {a.objectId}
                </span>
              ))}
              {(e.assignmentObjects ?? []).length > 3 && (
                <span className="text-[10px] text-muted-foreground">
                  {t('assignment.andMore', '+{{n}} more', { n: (e.assignmentObjects ?? []).length - 3 })}
                </span>
              )}
            </span>
          )}

          <span className="mt-1.5 flex flex-col gap-0.5">
            <span className="flex items-start gap-1.5 text-[11px] leading-snug" style={{ color: 'rgb(70,110,38)' }}>
              <svg className="mt-[3px] h-3 w-3 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m20 6-11 11-5-5" /></svg>
              {candidate.pro}
            </span>
            <span className="flex items-start gap-1.5 text-[11px] leading-snug" style={{ color: 'rgb(150,52,26)' }}>
              <svg className="mt-[3px] h-3 w-3 flex-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 8v5" /><path d="M12 16h.01" /><circle cx="12" cy="12" r="9" /></svg>
              {candidate.con}
            </span>
          </span>
        </span>
      </div>
    </button>
  )
}
